import { cache } from "react";
import { resolveApiBaseUrl } from "./apiBaseUrl";
import { normalizeSiteTheme, type SiteTheme } from "@/lib/regionTheme";

// settingsEndpoint is read from both the server (fetchSiteSettings below, via
// getSiteSettings) and the browser (settingsClient.ts's refreshClientSettings)
// — resolveApiBaseUrl() picks the right host for whichever is calling.
// METADATA_API_URL is an explicit override, but only applied server-side: a
// value like the Docker-internal `laravel.test` would be unreachable from a
// user's browser, so it must never leak into the client bundle's endpoint.
function resolveSettingsEndpoint(): string {
  if (typeof window === "undefined" && process.env.METADATA_API_URL) {
    return process.env.METADATA_API_URL;
  }

  return `${resolveApiBaseUrl()}/api/settings`;
}

export const settingsEndpoint = resolveSettingsEndpoint();

export const fallbackSiteName = "IJAAZAH";
export const fallbackMetadataDescription = "Generated by create next app";
export const fallbackFavicon = "/f/images/favicon.png";
export const siteSettingsTemplateId = "site-settings-data";
export const CLIENT_CACHE_TTL = 120000; // 2 minutes

export type SiteSettings = {
  /**
   * Whether each dashboard links to its user guide.
   *
   * A rollout switch an administrator holds, not a property of the guide's
   * content — both default to false, so a deploy never puts the guides in
   * front of anyone until somebody decides to.
   */
  userGuides: {
    student: boolean;
    teacher: boolean;
  };
  site: {
    name: string | null;
    tagline: string | null;
    favicon: string | null;
    logo: string | null;
    /** Variant for dark grounds (the footer); falls back to `logo`. */
    logoLight: string | null;
  };
  social: {
    facebook: string | null;
    instagram: string | null;
    x: string | null;
    youtube: string | null;
    linkedin: string | null;
    tiktok: string | null;
  };
  pageTitles: Record<string, string>;
  aboutUs: {
    hero: {
      title: string | null;
      content: string | null;
    };
    stats: Array<{
      image: string | null;
      number: string | null;
      title: string | null;
    }>;
    showMore: Array<{
      image: string | null;
      title: string | null;
      link: string | null;
    }>;
    gallery: Array<{
      image: string | null;
    }>;
  };
  contactPage: {
    address: {
      title: string | null;
      content: string | null;
    };
    phone: {
      title: string | null;
      value: string | null;
      whatsappLabel: string | null;
      whatsappValue: string | null;
    };
    email: {
      title: string | null;
      primaryValue: string | null;
      secondaryValue: string | null;
    };
    bannerImage: string | null;
    formSection: {
      title: string | null;
      description: string | null;
    };
  };
  teachers: {
    allowManualAvailability: boolean;
  };
  dashboardBanner: {
    enabled: boolean;
    message: string | null;
    messageHighlight: string | null;
    ctaText: string | null;
    ctaUrl: string | null;
    hideDays: number;
  };
  /**
   * The "$X Giveaway" referral promo (sidebar badge + Refer & Earn card).
   * `giveawayEnabled` is the ONLY visibility switch — there is no "active"
   * state. `giveawayAmount` is regional: `giveawayCurrency` is the ISO code
   * of the SAME region this whole payload is scoped to (resolved
   * server-side from Region::currency, never assumed to be USD) — pass both
   * together into formatGiveawayAmount(). `giveawayEndAt` is a full
   * ISO-8601 datetime with an explicit UTC offset (e.g.
   * "2026-10-15T23:59:00+00:00") and feeds nothing but the widget's
   * countdown — it must never be used to hide anything, even once it's in
   * the past.
   */
  referral: {
    giveawayEnabled: boolean;
    giveawayAmount: number;
    giveawayCurrency: string | null;
    giveawayEndAt: string | null;
  };
  /** Region-specific brand colors; see lib/regionTheme.ts. */
  theme: SiteTheme;
};

export const fetchSiteSettings = cache(async (locale: string, region: string | null): Promise<SiteSettings | null> => {
  try {
    let url = `${settingsEndpoint}?locale=${encodeURIComponent(locale)}`;
    if (region) {
      url += `&region_slug=${encodeURIComponent(region)}`;
    }
    const response = await fetch(
      url,
      {
        // Site name/social links/about-us/contact-page content changes rarely;
        // this was previously "no-store" (a fresh backend round-trip on every
        // single page render). The backend's own /api/settings response is
        // already cached for 15 minutes, so this just avoids re-fetching an
        // answer that hasn't changed within a much shorter window.
        next: { revalidate: 120 },
        headers: {
          Accept: "application/json",
        },
        signal: AbortSignal.timeout(15000),
      }
    );

    if (!response.ok) {
      return null;
    }

    const data: unknown = await response.json();
    return normalizeSiteSettings(data);
  } catch {
    return null;
  }
});

export function serializeSiteSettings(settings: SiteSettings | null): string {
  return JSON.stringify(settings);
}

export function buildDocumentTitle(siteName: string, titlePart: string | null): string {
  return titlePart ? `${siteName} | ${titlePart}` : siteName;
}

export function normalizeSiteSettings(payload: unknown): SiteSettings | null {
  const data = unwrapSettingsPayload(payload);
  if (!data) {
    return null;
  }

  const site = readRecord(data.site);
  const social = readRecord(data.social);
  const pageTitles = readRecord(data.page_titles);
  const aboutUs = readRecord(data.about_us);
  const contactPage = readRecord(data.contact_page);
  const dashboardBanner = readRecord(data.dashboard_banner);
  const referral = readRecord(data.referral);

  return {
    site: {
      name: readString(site?.name) ?? null,
      tagline: readString(site?.tagline) ?? null,
      favicon: readString(site?.favicon) ?? null,
      logo: readString(site?.logo) ?? null,
      logoLight: readString(site?.logo_light) ?? null,
    },
    social: {
      facebook: readString(social?.facebook) ?? null,
      instagram: readString(social?.instagram) ?? null,
      x: readString(social?.x) ?? null,
      youtube: readString(social?.youtube) ?? null,
      linkedin: readString(social?.linkedin) ?? null,
      tiktok: readString(social?.tiktok) ?? null,
    },
    pageTitles: pageTitles
      ? Object.fromEntries(
          Object.entries(pageTitles)
            .map(([key, value]) => [key, readString(value)])
            .filter((entry): entry is [string, string] => Boolean(entry[1]))
        )
      : {},
    aboutUs: {
      hero: {
        title: readString(readRecord(aboutUs?.hero)?.title) ?? null,
        content: readString(readRecord(aboutUs?.hero)?.content) ?? null,
      },
      stats: normalizeObjectArray(aboutUs?.stats).map((item) => ({
        image: readString(item.image) ?? null,
        number: readString(item.number) ?? null,
        title: readString(item.title) ?? null,
      })),
      showMore: normalizeObjectArray(aboutUs?.show_more).map((item) => ({
        image: readString(item.image) ?? null,
        title: readString(item.title) ?? null,
        link: readString(item.link) ?? null,
      })),
      gallery: normalizeObjectArray(aboutUs?.gallery).map((item) => ({
        image: readString(item.image) ?? null,
      })),
    },
    contactPage: {
      address: {
        title: readString(readRecord(contactPage?.address)?.title) ?? null,
        content: readString(readRecord(contactPage?.address)?.content) ?? null,
      },
      phone: {
        title: readString(readRecord(contactPage?.phone)?.title) ?? null,
        value: readString(readRecord(contactPage?.phone)?.value) ?? null,
        whatsappLabel: readString(readRecord(contactPage?.phone)?.whatsapp_label) ?? null,
        whatsappValue: readString(readRecord(contactPage?.phone)?.whatsapp_value) ?? null,
      },
      email: {
        title: readString(readRecord(contactPage?.email)?.title) ?? null,
        primaryValue: readString(readRecord(contactPage?.email)?.primary_value) ?? null,
        secondaryValue: readString(readRecord(contactPage?.email)?.secondary_value) ?? null,
      },
      bannerImage: readString(contactPage?.banner_image) ?? null,
      formSection: {
        title: readString(readRecord(contactPage?.form_section)?.title) ?? null,
        description: readString(readRecord(contactPage?.form_section)?.description) ?? null,
      },
    },
    teachers: {
      allowManualAvailability: Boolean(readRecord(data.teachers)?.allow_manual_availability),
    },
    dashboardBanner: {
      enabled: Boolean(dashboardBanner?.enabled),
      message: readString(dashboardBanner?.message) ?? null,
      messageHighlight: readString(dashboardBanner?.message_highlight) ?? null,
      ctaText: readString(dashboardBanner?.cta_text) ?? null,
      ctaUrl: readString(dashboardBanner?.cta_url) ?? null,
      hideDays: readNumber(dashboardBanner?.hide_days) ?? 1,
    },
    referral: {
      giveawayEnabled: Boolean(referral?.giveaway_enabled),
      giveawayAmount: readNumber(referral?.giveaway_amount) ?? 0,
      giveawayCurrency: readString(referral?.giveaway_currency) ?? null,
      giveawayEndAt: readString(referral?.giveaway_end_at) ?? null,
    },
    userGuides: {
      // Boolean(undefined) is false, so a backend that has not been updated
      // yet keeps the guides hidden rather than exposing them by accident.
      student: Boolean(readRecord(data.user_guides)?.student),
      teacher: Boolean(readRecord(data.user_guides)?.teacher),
    },
    theme: normalizeSiteTheme(data.theme),
  };
}

function unwrapSettingsPayload(data: unknown): Record<string, unknown> | null {
  if (!isRecord(data)) {
    return null;
  }

  if (isRecord(data.data)) {
    return data.data;
  }

  if (isRecord(data.settings)) {
    return data.settings;
  }

  if (isRecord(data.metadata)) {
    return data.metadata;
  }

  return data;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return isRecord(value) ? value : null;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function readNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function normalizeObjectArray(value: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord);
}
